/**
 * VÉRAULT Travels - AI Proposal System Components (ENHANCED)
 * 
 * HARDENED FIX + UX ENHANCEMENT:
 * 
 * Core Rules (UNCHANGED - No functional regression):
 * 1. Separate chat from proposals - two distinct layers
 * 2. Proposal Group Container with metadata
 * 3. Typed Proposal Cards (lodging, restaurant, activity, transfer)
 * 4. Consistent Action Bar on every card
 * 5. Responsive layout - stack vertically on mobile
 * 6. Apply behavior writes to itinerary immediately
 * 7. Fallback safety - invalid proposals render as plain text
 * 
 * NEW UX Enhancements:
 * - COLLAPSED by default (expand on click)
 * - Grouped by CATEGORY → Time of Day → Day
 * - Availability indicators (Available, Limited, Unavailable, Unknown)
 * - Advisor Override controls (Promote, Suppress, Replace)
 * - "Why this recommendation" rationale
 * - Availability confidence scoring (internal)
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, X, Edit2, MapPin, Clock, DollarSign, ExternalLink, Phone,
  Hotel, Utensils, Sparkles, Car, Calendar, ChevronDown, ChevronUp,
  Loader2, AlertCircle, CheckCircle, Plane, ArrowUp, EyeOff, RefreshCw,
  Info, AlertTriangle, HelpCircle, Star, Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// ============================================================
// CATEGORY ORDERING (Critical for day-aware planning)
// ============================================================
const CATEGORY_ORDER = [
  'transport',     // 1st - VIP pickup, airport transfer
  'transfer',      // Airport services, ground transport
  'flight',        // Flight recommendations
  'lodging',       // Hotels, accommodations
  'dining',        // Restaurants, bars, brunch
  'activity',      // Activities, experiences
  'experience',    // Tours, special experiences
  'misc'           // Enhancements, extras
];

// Time block ordering
const TIME_BLOCK_ORDER = ['morning', 'midday', 'afternoon', 'evening', 'night'];

// ============================================================
// PROPOSAL CARD TYPES & ICONS
// ============================================================
const PROPOSAL_TYPES = {
  lodging_proposal: { 
    label: 'Lodging', 
    icon: Hotel, 
    color: 'bg-purple-50 border-purple-200',
    accent: 'text-purple-600',
    category: 'lodging'
  },
  restaurant_proposal: { 
    label: 'Dining', 
    icon: Utensils, 
    color: 'bg-orange-50 border-orange-200',
    accent: 'text-orange-600',
    category: 'dining'
  },
  activity_proposal: { 
    label: 'Activity', 
    icon: Sparkles, 
    color: 'bg-blue-50 border-blue-200',
    accent: 'text-blue-600',
    category: 'activity'
  },
  experience_proposal: { 
    label: 'Experience', 
    icon: Sparkles, 
    color: 'bg-pink-50 border-pink-200',
    accent: 'text-pink-600',
    category: 'experience'
  },
  transfer_proposal: { 
    label: 'Transfer', 
    icon: Car, 
    color: 'bg-gray-50 border-gray-200',
    accent: 'text-gray-600',
    category: 'transfer'
  },
  transport_proposal: { 
    label: 'Transport', 
    icon: Car, 
    color: 'bg-emerald-50 border-emerald-200',
    accent: 'text-emerald-600',
    category: 'transport'
  },
  flight_proposal: { 
    label: 'Flight', 
    icon: Plane, 
    color: 'bg-sky-50 border-sky-200',
    accent: 'text-sky-600',
    category: 'flight'
  }
};

// Time block labels
const TIME_BLOCK_LABELS = {
  morning: 'Morning',
  midday: 'Midday',
  afternoon: 'Afternoon',
  evening: 'Evening',
  night: 'Night'
};

// ============================================================
// AVAILABILITY STATUS INDICATOR
// ============================================================
const AVAILABILITY_CONFIG = {
  available: {
    label: 'Available',
    color: 'text-[#8DA399] bg-[#8DA399]/10',
    icon: CheckCircle,
    clientLabel: 'Ready to book'
  },
  limited: {
    label: 'Limited',
    color: 'text-amber-600 bg-amber-50',
    icon: AlertTriangle,
    clientLabel: 'High demand for your dates'
  },
  unavailable: {
    label: 'Unavailable',
    color: 'text-[#B37E6B] bg-[#B37E6B]/10',
    icon: X,
    clientLabel: 'Alternative recommended'
  },
  unknown: {
    label: 'Unknown',
    color: 'text-[#8C8C8C] bg-[#F5F1EB]',
    icon: HelpCircle,
    clientLabel: 'Availability to be confirmed'
  }
};

// Confidence levels (internal)
const CONFIDENCE_LEVELS = {
  high: { label: 'High', weight: 3 },
  medium: { label: 'Medium', weight: 2 },
  low: { label: 'Low', weight: 1 }
};

function AvailabilityBadge({ status = 'unknown', compact = false }) {
  const config = AVAILABILITY_CONFIG[status] || AVAILABILITY_CONFIG.unknown;
  const Icon = config.icon;
  
  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  }
  
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs ${config.color}`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{config.label}</span>
    </div>
  );
}

// ============================================================
// URGENCY SIGNAL (Calm, non-alarmist)
// ============================================================
function UrgencySignal({ type = null }) {
  if (!type) return null;
  
  const signals = {
    limited_inventory: 'Limited availability',
    high_demand: 'High demand for these dates',
    short_window: 'Booking recommended soon',
    seasonal: 'Seasonal availability'
  };
  
  const message = signals[type] || type;
  
  return (
    <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg">
      <Zap className="w-3 h-3" />
      <span>{message}</span>
    </div>
  );
}

// ============================================================
// TYPED PROPOSAL CARD (ENHANCED - Collapsed by default)
// ============================================================
export function ProposalCard({
  proposal,
  onApprove,
  onReject,
  onEdit,
  onPromote,
  onSuppress,
  onReplace,
  applying = false,
  defaultExpanded = false  // NEW: Collapsed by default
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  // Determine proposal type and styling
  const typeKey = proposal.type ? `${proposal.type}_proposal` : 'activity_proposal';
  const typeConfig = PROPOSAL_TYPES[typeKey] || PROPOSAL_TYPES.activity_proposal;
  const Icon = typeConfig.icon;
  
  // Extract data from proposal
  const data = proposal.data || proposal;
  const title = data.name || data.title || 'Untitled';
  const category = typeConfig.label;
  const day = data.day;
  const timeBlock = data.section || data.time_block;
  const rationale = proposal.reason || data.rationale || data.description;
  const whyRecommended = proposal.why_recommended || data.why_recommended;
  const address = data.address;
  const websiteUrl = data.website_url;
  const phone = data.phone;
  const hours = data.hours;
  const priceRange = data.price_range;
  
  // Availability (new)
  const availability = proposal.availability || data.availability || 'unknown';
  const confidence = proposal.confidence || data.confidence || 'medium';
  const urgency = proposal.urgency || data.urgency;
  const isPromoted = proposal.promoted || false;
  const isSuppressed = proposal.suppressed || false;
  
  // Don't render suppressed items
  if (isSuppressed) return null;
  
  // One-line highlight for collapsed state
  const highlight = data.highlight || (rationale ? rationale.substring(0, 80) + (rationale.length > 80 ? '...' : '') : null);
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`rounded-xl border-2 ${typeConfig.color} overflow-hidden ${isPromoted ? 'ring-2 ring-[#C8A85A]' : ''}`}
      data-testid={`proposal-card-${proposal.id}`}
    >
      {/* COLLAPSED SUMMARY VIEW - Always visible */}
      <div 
        className="p-3 cursor-pointer hover:bg-white/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          {/* Type Icon */}
          <div className={`w-9 h-9 rounded-lg ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${typeConfig.accent}`} />
          </div>
          
          {/* Title & Summary */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <h4 className="font-medium text-sm text-[#1A1A1A] truncate">{title}</h4>
              {isPromoted && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#C8A85A] text-white uppercase">
                  Pinned
                </span>
              )}
            </div>
            
            {/* Metadata Row: Category | Day | Time | Availability */}
            <div className="flex items-center gap-2 text-[10px] text-[#8C8C8C] flex-wrap">
              <span className={`px-1.5 py-0.5 rounded ${typeConfig.color} ${typeConfig.accent} border uppercase tracking-wide`}>
                {category}
              </span>
              {day && (
                <span className="flex items-center gap-0.5">
                  <Calendar className="w-2.5 h-2.5" />
                  Day {day}
                </span>
              )}
              {timeBlock && (
                <span className="flex items-center gap-0.5">
                  <Clock className="w-2.5 h-2.5" />
                  {TIME_BLOCK_LABELS[timeBlock] || timeBlock}
                </span>
              )}
              {/* Availability Badge - visible in collapsed state */}
              <AvailabilityBadge status={availability} compact />
            </div>
            
            {/* One-line highlight */}
            {!expanded && highlight && (
              <p className="text-xs text-[#595959] mt-1 line-clamp-1">{highlight}</p>
            )}
          </div>
          
          {/* Expand/Collapse */}
          <button className="p-1 hover:bg-white/50 rounded flex-shrink-0">
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-[#8C8C8C]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#8C8C8C]" />
            )}
          </button>
        </div>
      </div>
      
      {/* EXPANDED DETAILS */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-current/10"
          >
            <div className="p-3 pt-2 space-y-2 bg-white/50">
              {/* Urgency Signal (if any) */}
              {urgency && <UrgencySignal type={urgency} />}
              
              {/* Full Rationale */}
              {rationale && (
                <p className="text-sm text-[#595959] leading-relaxed">{rationale}</p>
              )}
              
              {/* Why This Recommendation - TRANSPARENCY */}
              {whyRecommended && (
                <div className="flex items-start gap-2 text-xs text-[#8C8C8C] bg-[#F9F8F4] px-3 py-2 rounded-lg">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span><strong>Why recommended:</strong> {whyRecommended}</span>
                </div>
              )}
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {address && (
                  <div className="flex items-start gap-1.5 col-span-2">
                    <MapPin className="w-3.5 h-3.5 text-[#8C8C8C] flex-shrink-0 mt-0.5" />
                    <span className="text-[#595959]">{address}</span>
                  </div>
                )}
                
                {phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-[#8C8C8C]" />
                    <span className="text-[#595959]">{phone}</span>
                  </div>
                )}
                
                {hours && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-[#8C8C8C]" />
                    <span className="text-[#595959]">{hours}</span>
                  </div>
                )}
                
                {priceRange && (
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-[#C8A85A]" />
                    <span className="text-[#595959]">{priceRange}</span>
                  </div>
                )}
              </div>
              
              {/* Website Link */}
              {websiteUrl && (
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#C8A85A] hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-3 h-3" />
                  Official Website
                </a>
              )}
            </div>
            
            {/* ADVISOR OVERRIDE CONTROLS */}
            <div className="flex items-center gap-1 px-3 py-2 bg-[#F9F8F4]/50 border-t border-[#E6E4DE]/50">
              <button
                onClick={(e) => { e.stopPropagation(); onPromote?.(proposal.id); }}
                className={`p-1.5 rounded text-[10px] flex items-center gap-1 ${
                  isPromoted ? 'bg-[#C8A85A]/20 text-[#C8A85A]' : 'hover:bg-[#C8A85A]/10 text-[#8C8C8C]'
                }`}
                title="Pin to top"
              >
                <ArrowUp className="w-3 h-3" />
                {isPromoted ? 'Pinned' : 'Pin'}
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); onSuppress?.(proposal.id); }}
                className="p-1.5 rounded text-[10px] flex items-center gap-1 hover:bg-[#B37E6B]/10 text-[#8C8C8C]"
                title="Hide and don't show again"
              >
                <EyeOff className="w-3 h-3" />
                Hide
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); onReplace?.(proposal.id); }}
                className="p-1.5 rounded text-[10px] flex items-center gap-1 hover:bg-[#5B9BD5]/10 text-[#8C8C8C]"
                title="Get alternatives"
              >
                <RefreshCw className="w-3 h-3" />
                Alternatives
              </button>
            </div>
            
            {/* ACTION BAR - Consistent on every card */}
            <div className="flex items-center gap-2 p-3 bg-[#F9F8F4] border-t border-[#E6E4DE]">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); onReject?.(proposal.id); }}
                className="text-[#8C8C8C] hover:text-[#B37E6B] hover:bg-[#B37E6B]/10 h-8 text-xs"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Reject
              </Button>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); onEdit?.(proposal); }}
                className="text-[#8C8C8C] hover:text-[#5B9BD5] hover:bg-[#5B9BD5]/10 h-8 text-xs"
              >
                <Edit2 className="w-3.5 h-3.5 mr-1" />
                Edit
              </Button>
              
              <div className="flex-1" />
              
              {/* Only allow approve if available or explicitly approved unknown */}
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onApprove?.(proposal.id, proposal); }}
                disabled={applying || availability === 'unavailable'}
                className="bg-[#8DA399] hover:bg-[#7A9389] text-white h-8 text-xs"
                title={availability === 'unavailable' ? 'Cannot apply unavailable items' : 'Add to itinerary'}
              >
                {applying ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                ) : (
                  <Check className="w-3.5 h-3.5 mr-1" />
                )}
                Approve
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================================
// CATEGORY SECTION HEADER
// ============================================================
function CategoryHeader({ category, count, expanded, onToggle }) {
  const typeConfig = PROPOSAL_TYPES[`${category}_proposal`] || {
    label: category.charAt(0).toUpperCase() + category.slice(1),
    icon: Sparkles,
    accent: 'text-[#C8A85A]'
  };
  const Icon = typeConfig.icon;
  
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-3 bg-[#F9F8F4] hover:bg-[#F5F1EB] rounded-lg transition-colors"
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${typeConfig.accent}`} />
        <span className="font-medium text-sm text-[#1A1A1A]">{typeConfig.label}</span>
        <span className="text-xs text-[#8C8C8C]">({count})</span>
      </div>
      {expanded ? (
        <ChevronUp className="w-4 h-4 text-[#8C8C8C]" />
      ) : (
        <ChevronDown className="w-4 h-4 text-[#8C8C8C]" />
      )}
    </button>
  );
}

// ============================================================
// PROPOSAL GROUP CONTAINER (ENHANCED - Category-based)
// ============================================================
export function ProposalGroup({
  groupId,
  tripId,
  dayIndex,
  templateType = 'full_day',
  status = 'draft',
  proposals = [],
  onApproveItem,
  onRejectItem,
  onEditItem,
  onPromoteItem,
  onSuppressItem,
  onReplaceItem,
  onApplyDay,
  onApplyAll,
  applyingItem = null,
  applyingDay = false,
  applyingAll = false
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Group proposals by category (primary) -> time of day (secondary)
  const groupedByCategory = {};
  
  proposals.forEach(p => {
    const typeKey = p.type ? `${p.type}_proposal` : 'activity_proposal';
    const config = PROPOSAL_TYPES[typeKey] || { category: 'misc' };
    const category = config.category || 'misc';
    
    if (!groupedByCategory[category]) {
      groupedByCategory[category] = {};
    }
    
    const timeBlock = p.data?.section || p.data?.time_block || 'unscheduled';
    if (!groupedByCategory[category][timeBlock]) {
      groupedByCategory[category][timeBlock] = [];
    }
    
    groupedByCategory[category][timeBlock].push(p);
  });
  
  // Sort categories by order
  const sortedCategories = CATEGORY_ORDER.filter(cat => groupedByCategory[cat]);
  // Add any categories not in the order
  Object.keys(groupedByCategory).forEach(cat => {
    if (!sortedCategories.includes(cat)) {
      sortedCategories.push(cat);
    }
  });
  
  // Group title based on template type
  const groupTitles = {
    arrival: `Day ${dayIndex} — Arrival`,
    full_day: `Day ${dayIndex} — Full Day`,
    departure: `Day ${dayIndex} — Departure`,
    default: `Day ${dayIndex}`
  };
  const groupTitle = groupTitles[templateType] || groupTitles.default;
  
  // Count proposals
  const pendingCount = proposals.filter(p => p.status === 'pending' || !p.status).length;
  const appliedCount = proposals.filter(p => p.status === 'applied').length;
  
  if (proposals.length === 0) return null;
  
  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border-2 border-[#E6E4DE] bg-white overflow-hidden"
      data-testid={`proposal-group-${groupId}`}
    >
      {/* Group Header */}
      <div 
        className="px-4 py-3 bg-[#F9F8F4] border-b border-[#E6E4DE] cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#C8A85A]/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#C8A85A]" />
            </div>
            <div>
              <h3 className="font-serif text-base text-[#1A1A1A]">{groupTitle}</h3>
              <p className="text-xs text-[#8C8C8C]">
                {pendingCount} pending • {sortedCategories.length} categories
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full ${
              status === 'applied' 
                ? 'bg-[#8DA399]/10 text-[#8DA399]' 
                : status === 'approved'
                  ? 'bg-[#5B9BD5]/10 text-[#5B9BD5]'
                  : 'bg-[#C8A85A]/10 text-[#C8A85A]'
            }`}>
              {status === 'applied' ? 'Applied' : status === 'approved' ? 'Approved' : 'Draft'}
            </span>
            
            <button className="p-2 hover:bg-white rounded-lg">
              {collapsed ? (
                <ChevronDown className="w-4 h-4 text-[#8C8C8C]" />
              ) : (
                <ChevronUp className="w-4 h-4 text-[#8C8C8C]" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Proposals List - Grouped by Category */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <div className="p-3 space-y-3">
              {sortedCategories.map(category => {
                const categoryData = groupedByCategory[category];
                const categoryProposals = Object.values(categoryData).flat();
                const isExpanded = expandedCategories[category] !== false; // Default expanded
                
                return (
                  <div key={category} className="space-y-2">
                    {/* Category Header */}
                    <CategoryHeader 
                      category={category}
                      count={categoryProposals.length}
                      expanded={isExpanded}
                      onToggle={() => toggleCategory(category)}
                    />
                    
                    {/* Category Proposals */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="space-y-2 pl-2"
                        >
                          {/* Sort by time block within category */}
                          {TIME_BLOCK_ORDER.map(timeBlock => {
                            const blockProposals = categoryData[timeBlock];
                            if (!blockProposals || blockProposals.length === 0) return null;
                            
                            return (
                              <div key={timeBlock} className="space-y-2">
                                {/* Time block label (if multiple) */}
                                {Object.keys(categoryData).length > 1 && (
                                  <p className="text-[10px] text-[#8C8C8C] uppercase tracking-wide pl-1">
                                    {TIME_BLOCK_LABELS[timeBlock] || timeBlock}
                                  </p>
                                )}
                                
                                {/* Sort by promoted status, then availability */}
                                {blockProposals
                                  .sort((a, b) => {
                                    // Promoted first
                                    if (a.promoted && !b.promoted) return -1;
                                    if (!a.promoted && b.promoted) return 1;
                                    // Then by availability confidence
                                    const aConf = CONFIDENCE_LEVELS[a.confidence]?.weight || 2;
                                    const bConf = CONFIDENCE_LEVELS[b.confidence]?.weight || 2;
                                    return bConf - aConf;
                                  })
                                  .map(proposal => (
                                    <ProposalCard
                                      key={proposal.id}
                                      proposal={proposal}
                                      onApprove={onApproveItem}
                                      onReject={onRejectItem}
                                      onEdit={onEditItem}
                                      onPromote={onPromoteItem}
                                      onSuppress={onSuppressItem}
                                      onReplace={onReplaceItem}
                                      applying={applyingItem === proposal.id}
                                      defaultExpanded={false}
                                    />
                                  ))
                                }
                              </div>
                            );
                          })}
                          
                          {/* Unscheduled items */}
                          {categoryData['unscheduled'] && categoryData['unscheduled'].length > 0 && (
                            <div className="space-y-2">
                              <p className="text-[10px] text-[#8C8C8C] uppercase tracking-wide pl-1">
                                Unscheduled
                              </p>
                              {categoryData['unscheduled'].map(proposal => (
                                <ProposalCard
                                  key={proposal.id}
                                  proposal={proposal}
                                  onApprove={onApproveItem}
                                  onReject={onRejectItem}
                                  onEdit={onEditItem}
                                  onPromote={onPromoteItem}
                                  onSuppress={onSuppressItem}
                                  onReplace={onReplaceItem}
                                  applying={applyingItem === proposal.id}
                                  defaultExpanded={false}
                                />
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
            
            {/* GROUP ACTION BAR */}
            <div className="flex items-center gap-3 p-3 bg-[#F9F8F4] border-t border-[#E6E4DE]">
              {pendingCount > 0 && (
                <Button
                  onClick={() => onApplyDay?.(dayIndex)}
                  disabled={applyingDay}
                  className="bg-[#C8A85A] hover:bg-[#B39049] text-white text-sm"
                >
                  {applyingDay ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Apply Day {dayIndex} ({pendingCount})
                </Button>
              )}
              
              {onApplyAll && proposals.length > 0 && (
                <Button
                  variant="outline"
                  onClick={onApplyAll}
                  disabled={applyingAll}
                  className="text-sm"
                >
                  {applyingAll ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Apply All
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================================
// FALLBACK RENDERER (for malformed proposals)
// ============================================================
export function ProposalFallback({ content, error }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="rounded-lg border border-[#B37E6B]/30 bg-[#B37E6B]/5 p-3">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-[#B37E6B] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs font-medium text-[#B37E6B] mb-1">{error || 'Invalid proposal format'}</p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-[#8C8C8C] hover:underline"
          >
            {expanded ? 'Hide raw data' : 'Show raw data'}
          </button>
          
          {expanded && (
            <pre className="mt-2 p-2 bg-white/50 rounded text-[10px] overflow-x-auto max-h-32">
              {content}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HELPER: Group proposals by day
// ============================================================
export function groupProposalsByDay(proposals) {
  const groups = {};
  
  proposals.forEach(p => {
    const day = p.data?.day || 1;
    const templateType = p.data?.template_type || 'full_day';
    const groupId = `day-${day}-${templateType}`;
    
    if (!groups[groupId]) {
      groups[groupId] = {
        groupId,
        dayIndex: day,
        templateType,
        status: 'draft',
        proposals: []
      };
    }
    
    groups[groupId].proposals.push(p);
  });
  
  // Sort by day
  return Object.values(groups).sort((a, b) => a.dayIndex - b.dayIndex);
}

export default {
  ProposalCard,
  ProposalGroup,
  ProposalFallback,
  groupProposalsByDay,
  AvailabilityBadge,
  UrgencySignal,
  CATEGORY_ORDER,
  AVAILABILITY_CONFIG,
  CONFIDENCE_LEVELS
};
